import React, { useState } from 'react'
import { Dialog } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import Switch from 'components/atoms/Switch'
import Button from 'components/atoms/Button'
import { IoClose } from 'react-icons/io5'
import { find, some } from 'lodash'

const BranchTimingDialog = ({ isOpen, onClose, onSave, timings, onTimingChange, values, setFieldValue }) => {
  const { t } = useTranslation()
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  const handleRequestClose = () => {
    if (hasUnsavedChanges) return
    onClose()
  }

  const handleSave = () => {
    const timetable = Object.entries(timings).map(([day, value]) => ({
      day,
      active: value.enabled,
      time: value.hours === '24 hrs' ? '00:00:00' : value.hours,
    }))

    const existing24Mode = find(values?.timings, { mode: '24/7' }) || {}

    onSave([
      {
        mode: '24/7',
        isActive: existing24Mode.isActive ?? false,
        timetable,
      },
    ])

    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleRequestClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-6">
            <Dialog.Title className="text-xl font-semibold">{t('branchTimingDialog.title')}</Dialog.Title>
            <button onClick={handleRequestClose} disabled={hasUnsavedChanges} className={`p-1 ${hasUnsavedChanges ? 'opacity-40 cursor-not-allowed' : ''}`}>
              <IoClose />
            </button>
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg">{t('branchTimingDialog.mode24_7')}</h3>
              <Switch
                value={some(values.timings, { mode: '24/7', isActive: true })}
                onChange={e => {
                  setHasUnsavedChanges(true)
                  const existing24Mode = find(values?.timings, { mode: '24/7' }) || {}
                  setFieldValue('timings', [...(values.timings || []).filter(t => t.mode !== '24/7'), { ...existing24Mode, mode: '24/7', isActive: e }])
                }}
              />
            </div>
            <p className="text-gray-600 text-sm mt-2 mb-4">{t('branchTimingDialog.description')}</p>
          </div>

          <div className="space-y-4">
            {Object.entries(timings).map(([day, value]) => (
              <div key={day} className="flex items-center justify-between py-2">
                <span className="text-gray-700">{t(`branchTimingDialog.days.${day}`)}</span>
                <div className="flex items-center gap-4">
                  <Switch
                    value={value.enabled}
                    onChange={checked => {
                      setHasUnsavedChanges(true)
                      onTimingChange(day, 'enabled', checked)
                    }}
                  />
                  <span className="min-w-[60px] text-right">{t('branchTimingDialog.hours24')}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <Button onClick={handleRequestClose} disabled={hasUnsavedChanges} extraClassName="bg-gray-500 hover:bg-gray-600">
              {t('branchTimingDialog.buttons.cancel')}
            </Button>
            <Button onClick={handleSave}>{t('branchTimingDialog.buttons.saveChanges')}</Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  )
}

export default BranchTimingDialog
